import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styles from "../Home/HomePage.module.css";

const meterSortCollator = new Intl.Collator("en-ZA", {
  numeric: true,
  sensitivity: "base",
});

const compareMeterText = (left, right) => {
  const a = String(left || "").trim();
  const b = String(right || "").trim();

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return meterSortCollator.compare(a, b);
};
const authenticatedFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) {
    window.location.href = '/#/login';
    throw new Error('No auth token');
  }
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
  });
};

export default function MetersListPage() {
  localStorage.setItem("navbar_state", "user");

  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [isOwner, setIsOwner] = useState(false);

  //const API = "http://127.0.0.1:8000/api";
  const API = "https://simplix.co.za/api";
  const ESTIMATE_CACHE_TTL = 60 * 1000;

  const estimateCacheKey = (accountGuid) => `meter_estimate_${accountGuid}`;

  const getCachedEstimate = (accountGuid) => {
    try {
      const cached = JSON.parse(
        sessionStorage.getItem(estimateCacheKey(accountGuid)) || "null",
      );
      if (!cached || Date.now() - cached.ts > ESTIMATE_CACHE_TTL) return null;
      return cached.estimate || null;
    } catch {
      return null;
    }
  };

  const setCachedEstimate = (accountGuid, estimate) => {
    try {
      sessionStorage.setItem(
        estimateCacheKey(accountGuid),
        JSON.stringify({ estimate, ts: Date.now() }),
      );
    } catch {}
  };
  const fmtR = (v) => `R${(Number(v) || 0).toFixed(2)}`;

  const humanizeDays = (days) => {
    if (!Number.isFinite(days)) return "—";

    if (days < 1) {
      const hrs = Math.floor(days * 24);
      return `${hrs} hr${hrs !== 1 ? "s" : ""}`;
    }

    if (days < 14) {
      const d = Math.floor(days);
      const hrs = Math.floor((days - d) * 24);
      return `${d} day${d !== 1 ? "s" : ""}${
        hrs > 0 ? ` ${hrs} hr${hrs !== 1 ? "s" : ""}` : ""
      }`;
    }

    const weeks = days / 7;
    if (weeks < 8) {
      return `${weeks.toFixed(1)} weeks`;
    }

    const months = days / 30.4375;
    return `${months.toFixed(1)} months`;
  };

  const getMeterProvider = (meter) => {
    const sgc = (meter?.sgc ?? "").toString().toLowerCase();

    if (!sgc) return null;
    if (sgc.includes("broham")) return "broham";
    if (sgc.includes("stron") || sgc.includes("p4power")) return "p4power";

    return null;
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function loadMeters() {
      setLoading(true);
      setError("");

      try {

        const [ar, r, profileResponse] = await Promise.all([
  authenticatedFetch(`${API}/accounts`, { signal: controller.signal }),
  authenticatedFetch(`${API}/meters`, { signal: controller.signal }),
  authenticatedFetch(`${API}/profile`, { signal: controller.signal }),
]);

        const profileData = await profileResponse.json().catch(() => ({}));

        const ownerProfile =
          profileResponse.ok &&
          profileData?.found &&
          String(
            profileData?.profile?.typeOfUserLabel ||
              profileData?.profile?.new_typeofuser ||
              "",
          )
            .trim()
            .toLowerCase() === "owner";

        setIsOwner(ownerProfile);

        if (ar.ok) {
          const aj = await ar.json();
          const accounts = Array.isArray(aj?.accounts) ? aj.accounts : [];

          const hasPending = accounts.some(
            (a) => String(a?.status || "").toLowerCase() !== "active",
          );

          if (hasPending) {
            sessionStorage.setItem(
              "profile_notice",
              "Please upload your ID document for your pending account before using meters, wallets or payments.",
            );
            navigate("/profile", { replace: true });
            return;
          }
        }

        if (!r.ok) throw new Error("Failed to load meters");

        const j = await r.json();

        const baseMeters = (j.meters || []).map((m) => {
          const accountGuid =
            m.linkedAccountId || m.account_guid || m.linkedAccountGuid || null;

          const cachedEstimate = accountGuid
            ? getCachedEstimate(accountGuid)
            : null;

          return {
            id: m.id,
            meter_number: m.number || m.meter_number || m.name || "",
            alias: m.name || m.alias || "",
            building:
              m.building || m.buildingName || m.linkedBuildingName || "—",
            utility_type: m.type || m.utility_type || "",
            relayState: m.relayState || "Unknown",
            relayStateCode: m.relayStateCode ?? null,
            account_id: m.linkedAccountName || m.accountId || null,
            resident: m.resident || m.resident_name || null,
            sgc: m.sgc || "",
            accountGuid,

            walletBalance: cachedEstimate?.walletBalance ?? null,
            avgSpendPerDayR: cachedEstimate?.avgSpendPerDayR ?? null,
            estimatedDaysLeft: cachedEstimate?.estimatedDaysLeft ?? null,
            estimatedDaysLeftFloor:
              cachedEstimate?.estimatedDaysLeftFloor ?? null,
            estimatedDaysDisplay: cachedEstimate?.estimatedDaysDisplay ?? "—",
            estimateSubText: cachedEstimate?.estimateSubText ?? "Calculating…",
            needsAttention: cachedEstimate?.needsAttention ?? false,

            estimatedRemainingKwh:
              cachedEstimate?.estimatedRemainingKwh ?? null,
            estimatedRemainingKwhDisplay:
              cachedEstimate?.estimatedRemainingKwhDisplay ?? "—",
          };
        });

        if (cancelled) return;

        setRows(baseMeters);
        setLoading(false);

        if (baseMeters.length === 1 && !ownerProfile) {
          navigate(`/meters/${baseMeters[0].id}`, { replace: true });
          return;
        }

        const p4Meters = baseMeters.filter(
          (m) => getMeterProvider(m) === "p4power",
        );

        const meterDetailsById = new Map();

        await Promise.all(
          p4Meters.map(async (meter) => {
            try {
              const detailRes = await authenticatedFetch(`${API}/meters/${meter.id}/`, {
  signal: controller.signal,
});

              const detailJson = await detailRes.json().catch(() => ({}));

              if (detailRes.ok && detailJson?.meter) {
                meterDetailsById.set(meter.id, detailJson.meter);
              }
            } catch {}
          }),
        );

        const uniqueAccountGuids = [
          ...new Set(p4Meters.map((m) => m.accountGuid).filter(Boolean)),
        ];

        const ledgerByAccount = new Map();

        await Promise.all(
  uniqueAccountGuids.map(async (accountGuid) => {
    try {
      const br = await authenticatedFetch(
        `${API}/bc/customer-ledger/?account_id=${encodeURIComponent(accountGuid)}`,
        { signal: controller.signal }
      );

      if (br.status === 403) {
        // User doesn't have access to this account's billing - that's OK
        ledgerByAccount.set(accountGuid, null);
        return;
      }

      const js = await br.json().catch(() => ({}));

      if (!br.ok || !js?.ok) {
        throw new Error("Failed to load wallet estimate");
      }

      ledgerByAccount.set(accountGuid, js);
    } catch (walletError) {
      console.warn(`Wallet data not available for account ${accountGuid}`);
      ledgerByAccount.set(accountGuid, null);
    }
  }),
);

        const estimatesByMeterId = new Map();

        for (const meter of p4Meters) {
          const js = ledgerByAccount.get(meter.accountGuid);

          if (!js) {
            estimatesByMeterId.set(meter.id, {
              walletBalance: null,
              avgSpendPerDayR: null,
              estimatedDaysLeft: null,
              estimatedDaysLeftFloor: null,
              estimatedDaysDisplay: "—",
              estimateSubText: "Could not calculate",
              needsAttention: false,
              estimatedRemainingKwh: null,
              estimatedRemainingKwhDisplay: "—",
            });
            continue;
          }

          const walletBalance = Number(js.balance || 0);
          const entries = Array.isArray(js.entries) ? js.entries : [];

          const now = new Date();
          const start = new Date();
          start.setDate(now.getDate() - 29);

          let spendTotal = 0;
          const debitDates = [];

          for (const row of entries) {
            const d = row.postingDate ? new Date(row.postingDate) : null;
            const amt = Number(row.amount || 0);

            if (d && d >= start && d <= now && amt > 0) {
              spendTotal += amt;
              debitDates.push(d);
            }
          }

          let avgSpendPerDayR = 0;

          if (spendTotal > 0 && debitDates.length > 0) {
            debitDates.sort((a, b) => a - b);

            const firstDebit = debitDates[0];

            const actualDays =
              Math.floor(
                (now.getTime() - firstDebit.getTime()) / (1000 * 60 * 60 * 24),
              ) + 1;

            const effectiveDays = Math.min(Math.max(actualDays, 1), 30);

            avgSpendPerDayR = spendTotal / effectiveDays;
          }

          const detail = meterDetailsById.get(meter.id);
          const effectiveRateRPerKwh = Number(
            detail?.effectiveRateRPerKwh || 0,
          );

          const estimatedRemainingKwhRaw =
            walletBalance < 0 && effectiveRateRPerKwh > 0
              ? Math.abs(walletBalance) / effectiveRateRPerKwh
              : 0;

          const estimatedRemainingKwh = Math.max(0, estimatedRemainingKwhRaw);

          let estimate;

          if (!Number.isFinite(avgSpendPerDayR) || avgSpendPerDayR <= 0) {
            estimate = {
              walletBalance,
              avgSpendPerDayR,
              estimatedDaysLeft: null,
              estimatedDaysLeftFloor: null,
              estimatedDaysDisplay: "—",
              estimateSubText: "Not enough usage data yet",
              needsAttention: false,
              estimatedRemainingKwh,
              estimatedRemainingKwhDisplay: Number.isFinite(
                estimatedRemainingKwh,
              )
                ? `${estimatedRemainingKwh.toFixed(1)} kWh`
                : "—",
            };
          } else {
            const estimatedDaysLeft =
              walletBalance >= 0
                ? 0
                : Math.abs(walletBalance) / avgSpendPerDayR;

            const estimatedDaysLeftFloor = Number.isFinite(estimatedDaysLeft)
              ? Math.floor(estimatedDaysLeft)
              : null;

            estimate = {
              walletBalance,
              avgSpendPerDayR,
              estimatedDaysLeft,
              estimatedDaysLeftFloor,
              estimatedDaysDisplay:
                estimatedDaysLeftFloor != null
                  ? String(estimatedDaysLeftFloor)
                  : "—",
              estimateSubText: Number.isFinite(estimatedDaysLeft)
                ? humanizeDays(estimatedDaysLeft)
                : "Not enough usage data yet",
              needsAttention:
                walletBalance >= 0 ||
                (estimatedDaysLeftFloor != null &&
                  estimatedDaysLeftFloor <= 10),
              estimatedRemainingKwh,
              estimatedRemainingKwhDisplay: Number.isFinite(
                estimatedRemainingKwh,
              )
                ? `${estimatedRemainingKwh.toFixed(1)} kWh`
                : "—",
            };
          }

          estimatesByMeterId.set(meter.id, estimate);
          setCachedEstimate(meter.accountGuid, estimate);
        }

        if (cancelled) return;

        setRows((prev) =>
          prev.map((m) => {
            if (getMeterProvider(m) !== "p4power") {
              return {
                ...m,
                estimateSubText: "Not enough usage data yet",
              };
            }

            if (!m.accountGuid) {
              return {
                ...m,
                estimateSubText: "No linked account",
              };
            }

            return {
              ...m,
              ...(estimatesByMeterId.get(m.id) || {}),
            };
          }),
        );
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") {
          console.error(e);
          setError("Could not load meters");
          setLoading(false);
        }
      }
    }

    loadMeters();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [location.pathname, navigate]);

  const filtered = useMemo(() => {
    const searchText = q.trim().toLowerCase();

    const matchingRows = searchText
      ? rows.filter((meter) =>
          [
            meter.meter_number,
            meter.alias,
            meter.building,
            meter.utility_type,
            meter.account_id,
            meter.resident,
            meter.relayState,
          ]
            .join(" ")
            .toLowerCase()
            .includes(searchText),
        )
      : rows;

    return [...matchingRows].sort((left, right) => {
      const buildingComparison = compareMeterText(
        left.building,
        right.building,
      );

      if (buildingComparison !== 0) {
        return buildingComparison;
      }

      const unitComparison = compareMeterText(left.alias, right.alias);

      if (unitComparison !== 0) {
        return unitComparison;
      }

      return compareMeterText(left.meter_number, right.meter_number);
    });
  }, [rows, q]);

  return (
    <div className={styles.page}>
      <div className={`${styles.section} ${styles.metersPageSection}`}>
        <div className={styles.metersHero}>
          <div>
            <p className={styles.sectionEyebrow}>Meters</p>
            <h1 className={styles.h2}>My meters</h1>
            <p className={styles.sectionIntro}>
              All prepaid or postpaid electricity, water, or gas meters and view
              usage insights.
            </p>
            <button
              className={styles.primaryBtn}
              onClick={() => navigate("/meters/add")}
            >
              + Add Meter
            </button>
          </div>
        </div>

        <div className={styles.meterStatsGrid}>
          <div className={styles.meterStatCard}>
            <span>Total</span>
            <strong>{rows.length}</strong>
          </div>

          <div className={styles.meterStatCard}>
            <span>Electricity</span>
            <strong>
              {rows.filter((r) => r.utility_type === "Electricity").length}
            </strong>
          </div>

          <div className={styles.meterStatCard}>
            <span>Water</span>
            <strong>
              {rows.filter((r) => r.utility_type === "Water").length}
            </strong>
          </div>

          <div className={styles.meterStatCard}>
            <span>Gas</span>
            <strong>
              {rows.filter((r) => r.utility_type === "Gas").length}
            </strong>
          </div>
        </div>

        <div className={styles.metersSearchWrap}>
          <input
            className={`${styles.authInput} ${styles.metersSearchInput}`}
            placeholder="Search by meter no / alias / account / type"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {error && <div className={styles.noteBox}>{error}</div>}

        <div className={styles.metersTableCard}>
          <table className={`${styles.table} ${styles.metersTable}`}>
            <thead>
              <tr>
                <th>Meter</th>
                <th>Unit</th>
                <th>Building</th>
                <th>Resident</th>
                <th>Type</th>
                <th style={{ textAlign: "right" }}>Wallet Balance</th>
                <th>Est. kWh remaining</th>
                <th>Est. days remaining</th>
                <th>Status</th>
                <th>Wallet</th>
                {isOwner && <th>Settings</th>}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={isOwner ? 11 : 10}>Loading…</td>
                </tr>
              )}

              {!loading &&
                filtered.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <Link to={`/meters/${m.id}`} className={styles.inlineBtn}>
                        {m.meter_number}
                      </Link>
                    </td>
                    <td>{m.alias || "—"}</td>
                    <td>{m.building || "—"}</td>
                    <td>{m.resident || "—"}</td>
                    <td>{m.utility_type || "—"}</td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 900,
                        color:
                          m.walletBalance == null
                            ? "#6b7280"
                            : Number(m.walletBalance) < 0
                              ? "#139619"
                              : "#bb2323",
                      }}
                    >
                      {m.walletBalance == null
                        ? "Updating…"
                        : fmtR(m.walletBalance)}
                    </td>
                    <td>
                      <strong>{m.estimatedRemainingKwhDisplay || "—"}</strong>
                    </td>
                    <td>
                      <div
                        className={styles.meterEstimate}
                        style={{
                          background: m.needsAttention
                            ? "#fee2e2"
                            : "transparent",
                          border: m.needsAttention
                            ? "1px solid #ef4444"
                            : "1px solid transparent",
                        }}
                      >
                        <strong>{m.estimatedDaysDisplay}</strong>
                        <span>{m.estimateSubText}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.meterRelayBadge} ${
                          m.relayState === "On"
                            ? styles.meterRelayBadgeOn
                            : m.relayState === "Off"
                              ? styles.meterRelayBadgeOff
                              : styles.meterRelayBadgeUnknown
                        }`}
                      >
                        <span className={styles.meterRelayBadgeDot} />
                        {m.relayState}
                      </span>
                    </td>
                    <td>
                      {m.accountGuid ? (
                        <Link
                          to={`/accounts/${encodeURIComponent(m.accountGuid)}/wallet`}
                          className={`${styles.inlineBtn} ${styles.meterWalletBtn}`}
                          aria-label={`View wallet for meter ${m.meter_number}`}
                        >
                          Wallet
                        </Link>
                      ) : (
                        <span className={styles.meterActionUnavailable}>—</span>
                      )}
                    </td>
                    {isOwner && (
                      <td>
                        <Link
                          to={`/meters/${m.id}/settings`}
                          className={styles.inlineBtn}
                        >
                          Settings
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}

              {!loading && !filtered.length && (
                <tr>
                  <td colSpan={isOwner ? 11 : 10}>No meters found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
